import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

const SUDEPORTE_LOGO = "/brand/sudefootball-white-40.png";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (pwd !== pwd2) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo restablecer la contraseña");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md text-center">
          <AlertTriangle className="text-primary mx-auto mb-4" size={40} />
          <h2 className="font-heading font-black text-3xl uppercase tracking-tight">Enlace inválido</h2>
          <p className="text-sm text-muted-foreground mt-3">Falta el token de recuperación. Solicita uno nuevo desde el login.</p>
          <Link to="/login" className="btn-volt mt-6 inline-flex" data-testid="invalid-back-to-login">Volver al login <ArrowRight size={16} /></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 lg:p-16 bg-background">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-start leading-none mb-10">
          <img src={SUDEPORTE_LOGO} alt="Sudefootball" className="h-10 w-auto" />
          <span className="font-heading font-black italic tracking-tight leading-none text-white text-xs mt-2">sudeporte</span>
        </div>

        {!done ? (
          <>
            <div className="mb-10">
              <div className="overline mb-2">Nueva contraseña</div>
              <h2 className="font-heading font-black text-4xl tracking-tighter uppercase">Elige tu clave</h2>
              <p className="text-sm text-muted-foreground mt-3">
                Introduce tu nueva contraseña. Debe tener al menos 6 caracteres.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
              <div>
                <label className="overline block mb-2">Nueva contraseña</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  data-testid="reset-pwd-input"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-11 bg-transparent border border-border px-3 focus:border-primary focus:outline-none transition-colors duration-200 font-mono text-sm"
                />
              </div>
              <div>
                <label className="overline block mb-2">Confirmar contraseña</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  data-testid="reset-pwd2-input"
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-11 bg-transparent border border-border px-3 focus:border-primary focus:outline-none transition-colors duration-200 font-mono text-sm"
                />
              </div>

              <button
                type="submit"
                data-testid="reset-submit-btn"
                disabled={loading}
                className="btn-volt w-full justify-center"
              >
                {loading ? <Loader2 className="animate-spin" size={16} /> : <>Guardar contraseña <ArrowRight size={16} /></>}
              </button>
            </form>
          </>
        ) : (
          <div data-testid="reset-done-banner" className="border border-primary/60 bg-primary/5 p-6">
            <ShieldCheck className="text-primary mb-3" size={28} />
            <h2 className="font-heading font-black text-2xl uppercase tracking-tight mb-2">Contraseña actualizada</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Tu contraseña se ha guardado correctamente. Redirigiendo al login…
            </p>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-border">
          <Link
            to="/login"
            data-testid="reset-back-to-login-link"
            className="inline-flex items-center gap-2 text-xs font-heading uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors duration-200"
          >
            <ArrowLeft size={14} /> Volver al login
          </Link>
        </div>
      </div>
    </div>
  );
}
